import { Popover } from "@mui/material";
import React, { useCallback, useRef, useState } from "react";
import {
    EmojiActiveIcon,
    ComposerSmileIcon,
    InternalNoteBadgeLinesPencilIcon,
    InternalNoteBadgeMemoIcon,
} from "../../icon";
import { Contact } from "../../models/conversations";
import { UserModel } from "../../models/users.model";
import { MessageService } from "../../services/MessageService";
import { UtilityService } from "../../services/UtilityService";
import setToast from "../../helpers/toaster.helper";
import { ToasterType } from "../shared/toaster/toaster.model";
import CustomTooltip from "./CustomTooltip ";
import Emojis from "./emojis";
import InternalNoteComposerEditor from "./internal-note-composer-editor";

const INTERNAL_NOTE_MAX_CHARS = 500;

let debounceTimer: ReturnType<typeof setTimeout>;
const debounce = (callback: () => void, time: number) => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(callback, time);
};

function internalNoteMentionTagAttribute(u: UserModel): string {
    const compact = `${u.FirstName || ""} ${u.LastName || ""}`.trim().replace(/\s+/g, "");
    return compact || (u.Email || "user").replace(/[^\w]/g, "");
}

function internalNoteBuildTeamNotePayload(body: string, users: UserModel[]): { note: string; mentions: string[] } {
    const mentionOrder: UserModel[] = [];
    const seen = new Set<string>();
    const plain = body
        .replace(/\{\{_((?:u|U)[a-zA-Z0-9]+)_\}\}/g, (_, mentionId: string) => {
            const compact = mentionId.slice(1).toLowerCase();
            const user = users.find(
                (x) => !x.IsDeleted && (x.ID || "").replace(/-/g, "").toLowerCase() === compact
            );
            if (user?.ID && !seen.has(user.ID)) {
                seen.add(user.ID);
                mentionOrder.push(user);
            }
            return " ";
        })
        .replace(/[ \t]+\n/g, "\n")
        .replace(/\n[ \t]+/g, "\n")
        .replace(/[ \t]{2,}/g, " ")
        .trim();

    const tagBlock = mentionOrder
        .map((u) => {
            const attr = internalNoteMentionTagAttribute(u);
            const phone = (u.PhoneNumber || "").trim();
            return phone ? `{{_${attr}_}}{{_${phone}_}}` : `{{_${attr}_}}`;
        })
        .join("");

    return {
        note: plain + tagBlock,
        mentions: mentionOrder.map((u) => u.ID),
    };
}

export type InternalNoteComposerProps = {
    composerWrapRef?: React.RefObject<HTMLDivElement>;
    IsMobileDevice: boolean;
    IsNewMessage?: boolean;
    IsSecureChat?: boolean;
    IsAfterHours?: boolean;
    IsAfterHoursActive?: boolean;
    isDarkMode: boolean;
    Users: UserModel[];
    PhoneNumbers?: string[];
    ContactIDs?: string[];
    Contacts: Contact[];
    hasEmojis: boolean;
    onClose: () => void;
};

export default function InternalNoteComposer(props: InternalNoteComposerProps) {
    const [body, setBody] = useState("");
    const [bodyLength, setBodyLength] = useState(0);
    const [emoji, setEmoji] = useState("");
    const [anchorEmoji, setAnchorEmoji] = useState<HTMLElement | null>(null);
    const [onEmojiHover, setOnEmojiHover] = useState(false);
    const [isSendingMessage, setIsSendingMessage] = useState<boolean>(false)

    const emojiRef = useRef<HTMLButtonElement>(null);
    const composerAnchorRef = useRef<HTMLDivElement>(null);

    const resetHoverStates = useCallback((exceptHover = false) => {
        setOnEmojiHover(exceptHover);
    }, []);

    const debouncedHoverReset = useCallback(
        (activateHover = false) => {
            debounce(() => {
                resetHoverStates(activateHover);
            }, 50);
        },
        [resetHoverStates]
    );

    const handleClose = useCallback(() => {
        setBody("");
        setBodyLength(0);
        setEmoji("");
        setAnchorEmoji(null);
        setOnEmojiHover(false);
        props.onClose();
    }, [props]);

    const onBodyChange = useCallback((event: { target: { value: string } }, length?: number) => {
        const raw = (event.target.value ?? "").replace(/[\u200B-\u200D\uFEFF]/g, "");
        setBody(raw);
        setBodyLength(typeof length === "number" ? length : raw.length);
    }, []);

    const handleEmojiSelect = useCallback((e: { native: string }) => {
        setEmoji(e.native);
        setTimeout(() => setEmoji(""), 100);
        setAnchorEmoji(null);
        setOnEmojiHover(false);
    }, []);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.keyCode === 27) {
                handleClose();
                event.preventDefault();
            }
        },
        [handleClose]
    );

    const handlePostInternalNote = useCallback(() => {
        if (bodyLength > INTERNAL_NOTE_MAX_CHARS) {
            setToast(`Internal note cannot exceed ${INTERNAL_NOTE_MAX_CHARS} characters.`, ToasterType.DANGER);
            return;
        }
        if (!body?.trim()) {
            setToast("Add Internal Note before posting.", ToasterType.DANGER);
            return;
        }
        const contactNumber =
            props.PhoneNumbers && props.PhoneNumbers.length === 1
                ? props.PhoneNumbers[0]
                : UtilityService.unmaskPhoneNumber(
                    props.Contacts.find((x) => props.ContactIDs?.includes(x.ID))?.PhoneNumber || ""
                );
        if (!contactNumber?.trim()) {
            setToast("Cannot post note: no contact number.", ToasterType.DANGER);
            return;
        }
        const cleaned = body.replace(/[\u200B-\u200D\uFEFF]/g, "");
        const { note: notePayload, mentions } = internalNoteBuildTeamNotePayload(cleaned, props.Users || []);
        if (!notePayload.trim()) {
            setToast("Add Internal Note before posting.", ToasterType.DANGER);
            return;
        }
        setIsSendingMessage(true)
        MessageService.sendTeamNote({ contactNumber, mentions, note: notePayload })
            .then(() => {
                setToast("Internal note created successfully.", ToasterType.SUCCESS);
                setBody("");
                setBodyLength(0);
                setEmoji("");
                setAnchorEmoji(null);
                props.onClose();
                setIsSendingMessage(false)
            })
            .catch((error) => {
                setIsSendingMessage(false)
                if (error?.response?.data?.message) {
                    setToast(error.response.data.message, ToasterType.DANGER, "default");
                } else {
                    setToast("Failed to save internal note.", ToasterType.DANGER);
                }
            });
    }, [body, bodyLength, props]);

    const postDisabled = !body?.trim() || bodyLength > INTERNAL_NOTE_MAX_CHARS;

    const composerWrapClassName = `d-flex internal-note-composer-wrap br-5 bg-color-internal-note-wrap-bg color-internal-note-text b-around-internal-note-wrap-border o-hidden custom-border-box-size ${props.IsNewMessage
        ? props.IsMobileDevice
            ? "flex-column-reverse mx-1"
            : "flex-column-reverse mx-1"
        : props.IsMobileDevice
            ? "flex-direction mx-2"
            : `flex-direction RichEditor-root-border ${!props.IsAfterHours ? "mx-2" : ""}`
        } ${props.IsAfterHoursActive ? "disabled bg-color-settings-background" : ""}`;

    return (
        <>
            <div
                ref={props.composerWrapRef}
                className={composerWrapClassName}
            >
                <div
                    ref={composerAnchorRef}
                    className="d-flex flex-direction w-p-100 flex-1"
                >
                    <div className="flex-shrink-0 min-h-4 bg-color-internal-note-wrap-bg d-flex align-center justify-between px-2 py-1">
                        <div className="d-flex align-center flex-wrap">
                            <span className="internal-note-composer__badge lh-1 bg-color-internal-note-badge-bg d-inline-flex align-center justify-center custom-border-box-size">
                                <InternalNoteBadgeLinesPencilIcon className="flex-shrink-0 d-block w-3 h-3" aria-hidden />
                                <InternalNoteBadgeMemoIcon className="flex-shrink-0 w-2 h-2" aria-hidden />
                                <span className="font-bold lh-1 color-internal-note-text w-space d-inline-flex align-center m-0 p-0">INTERNAL NOTE</span>
                            </span>
                            <span className="internal-note-composer__subtitle color-internal-note-subtitle ml-1 h5-regular">Visible to your team only</span>
                        </div>
                        <button type="button" className="internal-note-composer__close bg-none lh-1 b-none c-pointer" onClick={handleClose} aria-label="Close internal note">
                            <span className="font-bold color-internal-note-text h2-semibold lh-1">×</span>
                        </button>
                    </div>
                    <div className="internal-note-composer__divider flex-shrink-0 w-p-100 bg-color-internal-note-divider b-none m-0" />
                    <div className={props.IsNewMessage ? "" : props.IsMobileDevice ? "d-flex" : "d-flex"}>
                        <div className="internal-note-composer__editor-wrap flex-1 bg-color-internal-note-editor-bg color-internal-note-text d-flex flex-direction w-fill h4-regular px-2 py-1 h-v-7 o-y-scroll">
                            <InternalNoteComposerEditor
                                composerAnchorRef={composerAnchorRef}
                                body={body}
                                users={props.Users || []}
                                placeholder="Write an internal note..."
                                emoji={emoji}
                                onKeyDown={handleKeyDown}
                                onChange={onBodyChange}
                            />
                        </div>
                    </div>
                    <div className="internal-note-composer__footer flex-shrink-0 w-p-100 bg-color-internal-note-wrap-bg amplify-grid custom-border-box-size align-center">
                        <div className="internal-note-composer__footer-left d-flex align-center">
                            {props.hasEmojis && (
                                <CustomTooltip title="Emoji" placement="bottom">
                                    <div className="my-auto">
                                        <button
                                            type="button"
                                            tabIndex={13}
                                            ref={emojiRef}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (anchorEmoji) {
                                                    setAnchorEmoji(null);
                                                } else {
                                                    setAnchorEmoji(emojiRef.current);
                                                }
                                            }}
                                            onMouseEnter={() => {
                                                debouncedHoverReset(true);
                                            }}
                                            onMouseLeave={() => {
                                                debouncedHoverReset();
                                            }}
                                        >
                                            {onEmojiHover || !!anchorEmoji ? (
                                                <EmojiActiveIcon className="c-pointer p-1" />
                                            ) : (
                                                <ComposerSmileIcon className="c-pointer p-1" />
                                            )}
                                        </button>
                                    </div>
                                </CustomTooltip>
                            )}
                        </div>
                        <div className="d-flex align-center justify-center">
                            {!props.IsMobileDevice && (
                                <div className="internal-note-composer__footer-message h7-regular lh-1 color-internal-note-footer-message d-inline-flex align-center flex-wrap justify-center t-align-center" role="status">
                                    <span>
                                        You&apos;re writing an internal note — this will{" "}
                                        <span className="internal-note-composer__warning-not font-bold t-decoration-underline">NOT</span> be sent to the contact
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="internal-note-composer__footer-actions d-flex align-center">
                            <button type="button" className="h5-semibold p-1 bg-none color-internal-note-discard b-none w-space c-pointer" onClick={handleClose}>
                                Discard
                            </button>
                            <button
                                type="button"
                                className="internal-note-composer__post-btn h5-semibold br-10 bg-color-internal-note-post-btn-bg color-internal-note-post-btn-text d-inline-flex align-center b-none c-pointer w-space"
                                onClick={handlePostInternalNote}
                                disabled={postDisabled || isSendingMessage}
                            >
                                Post Note
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {!props.IsSecureChat && !props.IsNewMessage && (
                <div className="d-flex align-center h5-regular justify-between mx-2 py-1">
                    <div className="d-flex align-center internal-note-composer__mention-hint-left min-w-0 t-align-center">
                        <span className="h7-regular flex-shrink-0 lh-1 min-w-0 br-5 bg-color-internal-note-mention-hint-icon-bg color-lite-dark-white w-2 h-2 d-inline-flex align-center justify-center" aria-hidden>
                            i
                        </span>
                        <span className={props.isDarkMode ? "" : "color-con-hist-msg-date"}>Type / to mention a teammate</span>
                    </div>
                    <p className={`m-0 pr-1 ${props.isDarkMode ? "" : "color-con-hist-msg-date"}`} aria-live="polite">
                        {`Characters: ${bodyLength}/${INTERNAL_NOTE_MAX_CHARS}`}
                    </p>
                </div>
            )}

            {Boolean(anchorEmoji) && (
                <Popover
                    open={Boolean(anchorEmoji)}
                    anchorEl={anchorEmoji}
                    onClose={() => {
                        setAnchorEmoji(null);
                        setOnEmojiHover(false);
                    }}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "center",
                    }}
                    transformOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                    }}
                >
                    <Emojis handleEmojiSelect={handleEmojiSelect} />
                </Popover>
            )}
        </>
    );
}
